/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import { Switch, Typography, Container, Grid2 } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import "./ApplicationDetailsMonetize.css";

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 32,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 18,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(15.5px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#1890ff",
        ...theme.applyStyles("dark", {
          backgroundColor: "#177ddc",
        }),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: "rgba(0,0,0,.25)",
    boxSizing: "border-box",
    ...theme.applyStyles("dark", {
      backgroundColor: "rgba(255,255,255,.35)",
    }),
  },
}));

function ApplicationDetailsMonetize({ postCallTheNewSubmission, teamId }) {
  const [isMonetized, setIsMonetized] = useState(false);
  const navigate = useNavigate();
  const { appId } = useParams();
  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );
  useEffect(() => {
    if (submissionData && Object.keys(submissionData).length > 0) {
      setIsMonetized(submissionData?.isMonetized);
    }
  }, [submissionData]);

  const changeIsMonetized = ($event) => {
    setIsMonetized($event.target.checked);
    let newSubmissionData = {
      ...submissionData,
      isMonetized: $event.target.checked,
    };
    delete newSubmissionData["legalDocuments"];
    postCallTheNewSubmission(newSubmissionData);
  };
  return (
    <Container maxWidth="lg">
      <Grid2 container justifyContent="flex-start" alignItems="center">
        <Grid2 xs={12} sm={12} md={12} lg={12}>
          <div
            id="monetizeApplication"
            className="monetize"
            style={{
              marginTop: "30px",
              marginLeft: "10%",
              width: submissionData.status === "DRAFT" ? "85%" : "90%",
            }}
          >
            <div className="monetize-wrapper-details">
              <div className="wrapper-diver">
                <Typography
                  sx={{
                    "font-weight": "bold !important",
                    "font-size": "20px !important",
                    color: "rgba(18, 20, 21, 1) !important",
                    marginLeft: "2px !important",
                  }}
                  className="uploadSignedApkText"
                >
                  Monetize App
                </Typography>

                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginRight: "15px" }}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: isMonetized ? "rgba(0, 0, 0, 0.6)" : "rgba(0, 0, 0, 0.87)",
                      fontWeight: isMonetized ? 400 : 500,
                    }}
                  >
                    Free
                  </Typography>
                  <AntSwitch
                    checked={isMonetized}
                    onChange={($event) => changeIsMonetized($event)}
                    disabled={submissionData.status !== "DRAFT"}
                  />
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: isMonetized ? "#0d7be3" : "rgba(0, 0, 0, 0.6)",
                      fontWeight: isMonetized ? 500 : 400,
                    }}
                  >
                    Paid
                  </Typography>
                </div>
              </div>
              <p className="monetize-text-small">
                Your app hasn't been published yet, so you can change whether it's
                free or paid. Once you've published, you can't change your app from
                free to paid.
              </p>
              
              {isMonetized && submissionData.status === "DRAFT" && (
                <div style={{ display: "flex", gap: "12px" }}>
                  <button
                    style={{
                      padding: "8px 16px",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#0d7be3",
                      backgroundColor: "transparent",
                      border: "1px solid #0d7be3",
                      borderRadius: "4px",
                      cursor: "pointer",
                      transition: "all 0.3s",
                    }}
                    onClick={() => {
                      navigate(`/ui/developer/applications/${appId}/premium-product`, {
                        state: { teamId: teamId },
                      });
                    }}
                  >
                    App Pricing
                  </button>
                  <button
                    style={{
                      padding: "8px 16px",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#0d7be3",
                      backgroundColor: "transparent",
                      border: "1px solid #0d7be3",
                      borderRadius: "4px",
                      cursor: "pointer",
                      transition: "all 0.3s",
                    }}
                    onClick={() => {
                      navigate(`/ui/developer/applications/${appId}/app-product`, {
                        state: { teamId: teamId },
                      });
                    }}
                  >
                    In-App Products
                  </button>
                </div>
              )}
            </div>
          </div>
        </Grid2>
      </Grid2>
    </Container>
  );
}

ApplicationDetailsMonetize.propTypes = {
  postCallTheNewSubmission: PropTypes.func.isRequired,
  teamId: PropTypes.string.isRequired,
};

export default ApplicationDetailsMonetize;